import * as dateFns from 'date-fns'
import { addMonths, parseISO } from 'date-fns'
import React from 'react'

import { getDate, getEarningsDateSuffix } from '../../../app/header/utils'
import { KeyStat } from '../../components/keystats/KeyStat'
import { KeyStats } from '../../components/keystats/KeyStats'
import { shortFormatDollar } from '../../util'
import { EARNINGS_DATE_FORMAT } from './constants'
import { EarningsData } from './types'
import { getFormattedPeriodValue, getIsEarningEstimate, getIsSemiAnnualEarning } from './utils'

type WidgetHeaderProps = Pick<EarningsData, 'earningsDate' | 'earningsData'>

export default function EarningsHeader({ earningsDate, earningsData }: WidgetHeaderProps) {
  const isSemiAnnualEarning = getIsSemiAnnualEarning(earningsData)
  const nextReportInfo = React.useMemo(() => {
    const parsedEarningsDate = earningsDate ? parseISO(earningsDate) : null
    const firstEstimateEarningIndex = earningsData.findIndex(getIsEarningEstimate)
    const firstEstimateEarning = earningsData[firstEstimateEarningIndex] ?? null
    const lastEarning = earningsData[firstEstimateEarningIndex - 1] ?? null

    if (lastEarning === null || firstEstimateEarning === null) {
      return {
        date: null,
        nextPeriod: null,
        dateSuffix: '',
        eps: null,
        gaapEps: null,
        revenue: null,
      }
    }

    const lastEarningDate = lastEarning.earningsDate
    const shouldShowEstimatedNextEarningsDate = !(
      parsedEarningsDate && getDate(parsedEarningsDate).getTime() >= Date.now()
    )
    const estimateEarningsDate = lastEarningDate
      ? addMonths(parseISO(lastEarningDate), isSemiAnnualEarning ? 6 : 3)
      : null

    return {
      date: shouldShowEstimatedNextEarningsDate ? estimateEarningsDate : parsedEarningsDate,
      nextPeriod: getFormattedPeriodValue(firstEstimateEarning.fiscalPeriod, isSemiAnnualEarning),
      dateSuffix: shouldShowEstimatedNextEarningsDate ? '(Est)' : getEarningsDateSuffix(parsedEarningsDate),
      eps: firstEstimateEarning?.epsEstimate,
      gaapEps: firstEstimateEarning.epsReportedEstimate,
      revenue: firstEstimateEarning?.salesEstimate,
    }
  }, [earningsDate, earningsData, isSemiAnnualEarning])

  return (
    <KeyStats className="my-6">
      <KeyStat
        datapoint={`${nextReportInfo.date ? dateFns.format(nextReportInfo.date, EARNINGS_DATE_FORMAT) : '—'} ${nextReportInfo.dateSuffix}`}
        label="Next report date"
      />
      <KeyStat datapoint={nextReportInfo.nextPeriod ?? '—'} label="Report period" />
      <KeyStat datapoint={shortFormatDollar(nextReportInfo.eps, { defaultValue: '—' })} label="EPS Est." />
      <KeyStat datapoint={shortFormatDollar(nextReportInfo.gaapEps, { defaultValue: '—' })} label="GAAP EPS Est." />
      <KeyStat
        datapoint={shortFormatDollar(nextReportInfo.revenue, { defaultValue: '—', multiply: 1_000_000 })}
        label="Revenue Est."
      />
    </KeyStats>
  )
}
